import { formatDate } from '../../../lib/i18n-format';
export type SectionLifecycle = 'upcoming' | 'active' | 'concluded' | 'archived';

export interface SectionLifecycleInput {
  startDate?: string | null;
  endDate?: string | null;
  lifecycleOverride?: 'active' | 'concluded' | null;
}

const ARCHIVE_DAYS_AFTER_END = 180;

function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Derive lifecycle state from section dates and optional admin override.
 *
 * Override semantics:
 *   - 'concluded' → always concluded regardless of dates.
 *   - 'active'    → always active, but archive threshold (endDate + 180d)
 *                   still applies once crossed.
 *
 * Date comparison is string-based on YYYY-MM-DD to avoid TZ drift.
 *
 * @param todayYmd Today as `yyyy-MM-dd` **in the tenant zone** (via `todayInZoneWeb`
 *   from `@dosfilos/shared`), NOT the browser's — a late-evening user in Chile
 *   (UTC-3) must not already read as "tomorrow". Mirrors the backend
 *   `Section.deriveLifecycle(todayYmd)`.
 */
export function deriveSectionLifecycle(
  section: SectionLifecycleInput,
  todayYmd: string,
): SectionLifecycle {
  const { startDate, endDate, lifecycleOverride } = section;

  if (lifecycleOverride === 'concluded') {
    if (endDate && todayYmd > addDaysYmd(endDate, ARCHIVE_DAYS_AFTER_END)) return 'archived';
    return 'concluded';
  }

  if (endDate && todayYmd > addDaysYmd(endDate, ARCHIVE_DAYS_AFTER_END)) return 'archived';

  if (lifecycleOverride === 'active') return 'active';

  if (startDate && todayYmd < startDate) return 'upcoming';
  if (endDate && todayYmd > endDate) return 'concluded';
  return 'active';
}

export function formatDateEs(ymd: string | null | undefined): string {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return formatDate(dt, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateShortEs(ymd: string | null | undefined): string {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return formatDate(dt, { day: 'numeric', month: 'short' });
}

/** Lo que «Mis cursos» muestra: lo mismo que el inicio —en marcha, por empezar
 *  o recién terminado—. Lo archivado vive en Historial; un alumno importado con
 *  años de historia vería, si no, decenas de cursos viejos (y el mismo curso
 *  repetido) en vez de lo que está cursando. */
export function sectionsForCoursesIndex<T extends SectionLifecycleInput>(
  sections: readonly T[],
  todayYmd: string,
): { shown: T[]; archived: number } {
  const shown: T[] = [];
  let archived = 0;
  for (const s of sections) {
    if (deriveSectionLifecycle(s, todayYmd) === 'archived') archived += 1;
    else shown.push(s);
  }
  return { shown, archived };
}

/** «Mis cursos», agrupado con la MISMA regla de ciclo de vida que el Inicio:
 *  arriba lo que está en curso o por empezar; aparte, en «Cursos pasados», lo
 *  terminado (concluded). Lo archivado no se muestra: vive en Historial. */
export function coursesIndexGroups<T extends SectionLifecycleInput>(
  sections: readonly T[],
  todayYmd: string,
): {
  current: Array<{ section: T; lifecycle: 'active' | 'upcoming' }>;
  past: T[];
  archived: number;
} {
  const current: Array<{ section: T; lifecycle: 'active' | 'upcoming' }> = [];
  const past: T[] = [];
  let archived = 0;
  for (const s of sections) {
    const lc = deriveSectionLifecycle(s, todayYmd);
    if (lc === 'archived') archived += 1;
    else if (lc === 'concluded') past.push(s);
    else current.push({ section: s, lifecycle: lc });
  }
  return { current, past, archived };
}
